<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { useContactVoltalisDialogStore } from "@/stores/contact-voltalis-dialog/contact-voltalis-dialog-store";
import { branded } from "@/utils/branded-validation-utils";
import { useContactUrl } from "@/utils/support-utils";

import Dialog from "primevue/dialog";
import Select from "primevue/select";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const { t, k } = useTypedIntl();

const contactStore = useContactVoltalisDialogStore();
const { visible } = storeToRefs(contactStore);

const { data: sites, isPending, isError: sitesError } = useGetSitesQuery();

const selectedSiteId = ref<V4HSiteId>();

const { contactUrl } = useContactUrl(selectedSiteId);

const siteOptions = computed(() => {
  if (isPending.value || !sites.value) {
    return [];
  }

  return sites.value.map((site) => {
    return {
      label: site.name,
      value: site.siteId,
    };
  });
});

watch(visible, (newValue) => {
  if (newValue) {
    MixpanelService.trackEvent(EMixpanelEvent.CONTACT_VOLTALIS);
  }
});

watch(
  sites,
  (nextSites) => {
    if (nextSites === undefined || nextSites.length === 0) {
      return;
    }

    if (selectedSiteId.value !== undefined) {
      return;
    }

    selectedSiteId.value = branded.v4hSiteId.parse(nextSites[0].siteId);
  },
  { immediate: true },
);

watch(sitesError, (error) => {
  toastKiliError(error);
});
</script>

<template>
  <Dialog
    v-model:visible="visible"
    class="md:min-w-[20rem] md:w-[40%]"
    modal
  >
    <template #header>
      <div class="flex flex-col">
        <h4 class="flex w-full font-semibold">
          {{ t(k.components["contact-voltalis-dialog"].title) }}
        </h4>
        <span class="text-text-secondary">
          {{ t(k.components["contact-voltalis-dialog"].subtitle) }}
        </span>
      </div>
    </template>

    <label
      id="site-id-select-label"
      class="font-medium"
      for="site-id-select"
    >
      {{ t(k.components["contact-voltalis-dialog"].form["site-id"].label) }}
    </label>
    <Select
      id="site-id-select-label"
      v-model.lazy="selectedSiteId"
      aria-label="site-id-select-help-text"
      aria-labelledby="site-id-select-label"
      autofocus
      class="w-full"
      data-test-id="contact-voltalis-select-site"
      :disabled="isPending"
      :loading="isPending"
      option-label="label"
      option-value="value"
      :options="siteOptions"
    />

    <template #footer>
      <div class="grid grid-flow-col grid-cols-2 gap-2 w-full">
        <HospitalityButton
          data-test-id="contact-voltalis-cancel-button"
          :label="t(k.global.misc.cancel)"
          severity="secondary"
          @click="visible = false"
        />
        <a
          v-if="selectedSiteId"
          target="_blank"
          :href="contactUrl"
        >
          <HospitalityButton
            class="w-full flex"
            data-test-id="contact-voltalis-submit-button"
            :label="t(k.global.misc.next)"
          />
        </a>
        <HospitalityButton
          v-else
          disabled
          class="w-full flex"
          data-test-id="contact-voltalis-disabled-submit-button"
          :label="t(k.global.misc.next)"
        />
      </div>
    </template>
  </Dialog>
</template>
